/**
 * Global theme behavior: header scroll state, mobile nav, announcement bar
 * dismiss, predictive search toggle, scroll-reveal, FAQ accordion, hero
 * slider, testimonials slider, Instagram gallery, shop filters, and cart
 * quantity/remove actions.
 *
 * Product add-to-cart + quick view live in product-grid.js (shared by every
 * section that renders snippets/card-product.liquid) — see that file for why
 * it is kept separate rather than duplicated per section.
 */
(function () {
  'use strict';

  /* ---- Sticky header state ---- */
  var header = document.querySelector('[data-site-header]');
  if (header && header.hasAttribute('data-sticky-header')) {
    var onScroll = function () {
      header.classList.toggle('scrolled', window.scrollY > 40);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---- Mobile nav: accessible open/close with focus restore ---- */
  (function () {
    var toggle = document.getElementById('menuToggle');
    var nav = document.getElementById('navLinks');
    if (!toggle || !nav) return;

    function closeMenu() {
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    }
    function openMenu() {
      nav.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
    }
    toggle.addEventListener('click', function () {
      nav.classList.contains('is-open') ? closeMenu() : openMenu();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) closeMenu();
    });
    nav.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', function () {
        if (nav.classList.contains('is-open')) {
          nav.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
        }
      });
    });
  })();

  /* ---- Announcement bar dismiss ---- */
  (function () {
    var bar = document.querySelector('[data-announcement-bar][data-dismissible]');
    if (!bar) return;
    var key = 'announcement-bar-dismissed';
    if (sessionStorage.getItem(key) === '1') {
      bar.remove();
      return;
    }
    var dismissBtn = bar.querySelector('[data-announcement-dismiss]');
    if (dismissBtn) {
      dismissBtn.addEventListener('click', function () {
        sessionStorage.setItem(key, '1');
        bar.remove();
      });
    }
  })();

  /* ---- Predictive search toggle ---- */
  (function () {
    var toggle = document.querySelector('[data-predictive-search-toggle]');
    var panel = document.getElementById('headerSearch');
    if (!toggle || !panel) return;
    toggle.addEventListener('click', function () {
      var isHidden = panel.hasAttribute('hidden');
      if (isHidden) {
        panel.removeAttribute('hidden');
        toggle.setAttribute('aria-expanded', 'true');
        var input = panel.querySelector('[data-predictive-search-input]');
        if (input) input.focus();
      } else {
        panel.setAttribute('hidden', '');
        toggle.setAttribute('aria-expanded', 'false');
      }
    });
  })();

  /* ---- Scroll reveal ---- */
  (function () {
    var targets = document.querySelectorAll('.reveal, .reveal-l, .reveal-r');
    if (!targets.length) return;
    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('in'); });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    targets.forEach(function (el) { io.observe(el); });
  })();

  /* ---- FAQ accordion ---- */
  (function () {
    var wrap = document.querySelector('[data-faq-accordion]');
    if (!wrap) return;
    var items = wrap.querySelectorAll('.faq-item');

    function setHeight(item) {
      var answer = item.querySelector('.faq-a');
      var btn = item.querySelector('.faq-q');
      var isActive = item.classList.contains('active');
      answer.style.maxHeight = isActive ? answer.scrollHeight + 'px' : '0px';
      if (btn) btn.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    }

    items.forEach(function (item) {
      setHeight(item);
      item.querySelector('.faq-q').addEventListener('click', function () {
        var wasActive = item.classList.contains('active');
        items.forEach(function (i) { i.classList.remove('active'); setHeight(i); });
        if (!wasActive) { item.classList.add('active'); setHeight(item); }
      });
    });
    window.addEventListener('resize', function () { items.forEach(setHeight); });
  })();

  /* ---- Hero slider ---- */
  (function () {
    var hero = document.querySelector('[data-hero-slider]');
    if (!hero) return;

    var copies = hero.querySelectorAll('.hero-slide-copy');
    var bgImages = hero.querySelectorAll('.hero-bg-image');
    var dots = hero.querySelectorAll('[data-hero-dot]');
    var counter = hero.querySelector('[data-hero-current]');
    var heroInner = hero.querySelector('.hero-inner');
    var autoplayMs = parseInt(hero.getAttribute('data-autoplay-ms'), 10) || 6000;
    var idx = 0;
    var timer;

    function setSlide(n) {
      var count = copies.length;
      if (!count) return;
      idx = (n + count) % count;

      if (heroInner) heroInner.classList.add('switching');
      setTimeout(function () {
        copies.forEach(function (copy, i) { copy.hidden = i !== idx; });
        bgImages.forEach(function (img, i) { img.classList.toggle('active', i === idx); });
        dots.forEach(function (dot, i) { dot.classList.toggle('active', i === idx); });
        if (counter) counter.textContent = String(idx + 1).padStart(2, '0');
        if (heroInner) heroInner.classList.remove('switching');
      }, 220);
    }

    function resetTimer() {
      clearInterval(timer);
      timer = setInterval(function () { setSlide(idx + 1); }, autoplayMs);
    }

    var prevBtn = hero.querySelector('[data-hero-prev]');
    var nextBtn = hero.querySelector('[data-hero-next]');
    if (prevBtn) prevBtn.addEventListener('click', function () { setSlide(idx - 1); resetTimer(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { setSlide(idx + 1); resetTimer(); });
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { setSlide(i); resetTimer(); });
    });

    if (copies.length > 1) resetTimer();
  })();

  /* ---- Testimonials slider ---- */
  (function () {
    var root = document.querySelector('testimonials-slider');
    if (!root) return;
    var slides = root.querySelectorAll('[data-t-slide]');
    var visuals = root.querySelectorAll('[data-t-visual]');
    var bars = root.querySelectorAll('[data-t-dot]');
    var counter = root.querySelector('[data-t-current]');
    var idx = 0;
    var timer;

    function go(n) {
      var count = slides.length;
      if (!count) return;
      slides[idx].classList.remove('active');
      if (visuals[idx]) visuals[idx].classList.remove('active');
      if (bars[idx]) bars[idx].classList.remove('active');
      idx = (n + count) % count;
      slides[idx].classList.add('active');
      if (visuals[idx]) visuals[idx].classList.add('active');
      if (bars[idx]) bars[idx].classList.add('active');
      if (counter) counter.textContent = String(idx + 1).padStart(2, '0');
      resetTimer();
    }
    function resetTimer() {
      clearInterval(timer);
      timer = setInterval(function () { go(idx + 1); }, 5500);
    }

    var prevBtn = root.querySelector('[data-t-prev]');
    var nextBtn = root.querySelector('[data-t-next]');
    if (prevBtn) prevBtn.addEventListener('click', function () { go(idx - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { go(idx + 1); });
    bars.forEach(function (bar, i) { bar.addEventListener('click', function () { go(i); }); });

    if (slides.length > 1) resetTimer();
  })();

  /* ---- Instagram gallery arrows ---- */
  (function () {
    var track = document.querySelector('[data-insta-track]');
    if (!track) return;
    var prevBtn = document.querySelector('[data-insta-prev]');
    var nextBtn = document.querySelector('[data-insta-next]');
    function step() {
      var item = track.querySelector('.insta-item');
      return item ? item.offsetWidth + 18 : 300;
    }
    if (nextBtn) nextBtn.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
    if (prevBtn) prevBtn.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
  })();

  /* ---- Shop category filter ---- */
  (function () {
    var group = document.querySelector('[data-product-filters]');
    if (!group) return;
    var buttons = group.querySelectorAll('.filter-btn');
    var cards = document.querySelectorAll('[data-product-card]');
    var announce = document.querySelector('[data-filter-announce]');

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        buttons.forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
        var filter = btn.dataset.filter;
        var visible = 0;
        cards.forEach(function (card) {
          var match = filter === 'all' || card.dataset.category === filter;
          card.classList.toggle('is-hidden', !match);
          if (match) visible++;
        });
        if (announce) {
          announce.textContent = 'Showing ' + visible + ' product' + (visible === 1 ? '' : 's') + ' in ' + btn.textContent.trim();
        }
      });
    });
  })();

  /* ---- Cart page: quantity update + line removal via Ajax Cart API ---- */
  (function () {
    var form = document.getElementById('CartForm');
    if (!form) return;

    function updateCart(line, quantity) {
      fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (res) { return res.json(); })
        .then(function () { window.location.reload(); })
        .catch(function () { form.submit(); });
    }

    form.querySelectorAll('[data-cart-quantity]').forEach(function (input) {
      input.addEventListener('change', function () {
        updateCart(input.dataset.cartQuantity, parseInt(input.value, 10) || 0);
      });
    });
    form.querySelectorAll('[data-cart-remove]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        updateCart(btn.dataset.cartRemove, 0);
      });
    });
  })();

  /* ---- Product page: simple variant option -> variant id / price sync ---- */
  (function () {
    var form = document.getElementById('ProductForm');
    if (!form) return;
    var variantScript = form.querySelector('[data-product-variants]');
    var selectors = form.querySelectorAll('[data-option-selector]');
    var hiddenId = form.querySelector('[data-product-variant-id]');
    var priceEl = document.querySelector('[data-product-price]');
    if (!variantScript || !selectors.length) return;

    var variants;
    try {
      variants = JSON.parse(variantScript.textContent);
    } catch (e) {
      return;
    }

    function currentOptions() {
      var values = [];
      selectors.forEach(function (select) {
        values[parseInt(select.dataset.optionIndex, 10) - 1] = select.value;
      });
      return values;
    }

    function findMatch() {
      var options = currentOptions();
      return variants.find(function (variant) {
        return variant.options.every(function (opt, i) { return opt === options[i]; });
      });
    }

    function applyVariant(variant) {
      if (!variant) return;
      if (hiddenId) hiddenId.value = variant.id;
      var addBtn = form.querySelector('[data-add-to-cart]');
      if (addBtn) {
        addBtn.dataset.variantId = variant.id;
        addBtn.disabled = !variant.available;
      }
      if (priceEl && variant.price != null) {
        priceEl.textContent = window.Shopify && window.Shopify.formatMoney
          ? window.Shopify.formatMoney(variant.price)
          : '$' + (variant.price / 100).toFixed(2);
      }
    }

    selectors.forEach(function (select) {
      select.addEventListener('change', function () { applyVariant(findMatch()); });
    });
  })();
})();
